/**
 * 生命健康 H · 检测报告（二级页）
 *
 * 产品判断：
 *   报告不是「上传完就事」，而是 H-Score™ 的证据链——列表要能看清哪一份是当前基准、
 *   哪一份已解析、哪一份正在解析、哪一份解析失败及原因。
 *
 * 解析链路（已接入，异步）：
 *   建档 → 上传报告文件 → 触发解析 → 外部解析服务（OCR + 结构化抽取）→ 校验 → 指标入档 → 通知。
 *   因此本页必须先引导「上传文件」，再引导「开始解析」；解析中要轮询等待，
 *   解析失败要**如实展示后端给的原因**。
 *
 * 诚实边界（本页最重要的一条）：
 *   绝不展示任何假装的解析结果。未接入解析服务时报告会停在「待解析」，
 *   界面上就显示「待解析」并说明原因——在健康产品里，「失败但看起来正常」比明显报错危险得多。
 */
import { useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { HealthReport } from '@hew3/shared';
import { ApiError, api } from '../../lib/api.ts';
import { trackAction, trackFunnel } from '../../lib/analytics.ts';
import { formatDate, formatDateTime } from '../../lib/format.ts';
import { useUi } from '../../store/ui.ts';
import { Badge, Button, Card, Sheet, StateBox } from '../../components/ui.tsx';

/** POST /api/health/reports 的返回形状（见 apps/api/src/modules/health.routes.ts） */
interface SubmittedReport {
  id: string;
  title: string;
  parsed: boolean;
  parseStatus: string;
  hasFile: boolean;
  status: string;
  note: string;
}

/** PUT /api/health/reports/:id/file 的返回形状 */
interface UploadedFile {
  id: string;
  fileName: string;
  sizeBytes: number;
  sha256: string;
}

/** 与后端 allowedMimeTypes 对齐；前端只做「提前告知」，最终校验仍在服务端 */
const acceptAttr = '.pdf,.jpg,.jpeg,.png,.webp,.heic';
const allowedMimes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/heic'];
const maxFileBytes = 20 * 1024 * 1024;

/** 按扩展名兜底推断 MIME：部分浏览器（尤其 HEIC）file.type 为空 */
function inferMime(file: File): string {
  if (file.type && allowedMimes.includes(file.type)) return file.type;
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  const byExt: Record<string, string> = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    heic: 'image/heic',
  };
  return byExt[ext] ?? '';
}

/** 解析状态 → 徽标。不用 parsed 布尔值：它无法区分「解析中」与「解析失败」 */
function parseBadge(status: HealthReport['parseStatus']): { tone: 'ok' | 'warn' | 'danger' | 'ghost'; text: string } {
  if (status === 'parsed') return { tone: 'ok', text: '已解析' };
  if (status === 'parsing') return { tone: 'warn', text: '解析中' };
  if (status === 'failed') return { tone: 'danger', text: '解析失败' };
  return { tone: 'warn', text: '待解析' };
}

/** 报告日期输入框默认值：本地当天，仅作为表单默认值，不作为业务数据 */
function todayInput(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function HealthReportsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showToast = useUi((s) => s.showToast);

  const reportsQuery = useQuery({
    queryKey: ['health', 'reports'],
    queryFn: () => api.get<HealthReport[]>('/api/health/reports'),
    // 解析是异步的：只要还有报告在解析中，就持续拉取，完成后自动停在最新状态。
    // 用 react-query 的 refetchInterval（而不是手写 setInterval），
    // 组件卸载与查询失效都由它统一管理。
    refetchInterval: (query) => {
      const data = query.state.data as HealthReport[] | undefined;
      return data?.some((r) => r.parseStatus === 'parsing') ? 3000 : false;
    },
  });

  const [title, setTitle] = useState('');
  const [institution, setInstitution] = useState('');
  const [reportedAt, setReportedAt] = useState(todayInput);
  const [desc, setDesc] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [queued, setQueued] = useState<SubmittedReport | null>(null);

  // 文件选择：同一份文件要能重复选（onChange 不会对相同文件再次触发，故每次选完清空 value）
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadForRef = useRef<string | null>(null);

  const createReport = useMutation({
    mutationFn: (body: { title: string; institution: string; reportedAt: string; desc?: string }) =>
      api.post<SubmittedReport>('/api/health/reports', body),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['health', 'reports'] });
      void queryClient.invalidateQueries({ queryKey: ['health'] });
      showToast('报告已建档，请上传报告文件后开始解析');
      // 漏斗第一步：建档。与「上传文件」「开始解析」构成报告解析漏斗，
      // 用于观察成员到底卡在哪一步（当前经验是上传这一步摩擦最大）。
      trackFunnel('report_parse', 'report_created');
      setTitle('');
      setInstitution('');
      setReportedAt(todayInput());
      setDesc('');
      setFormError(null);
      // 用底部弹层显示接口返回的 note，明确「已建档但还没上传文件」
      setQueued(result);
    },
    onError: (error) => {
      showToast(error instanceof ApiError ? error.message : '报告提交失败，请稍后重试', 'error');
    },
  });

  const uploadFile = useMutation({
    mutationFn: (input: { id: string; fileName: string; mimeType: string; contentBase64: string }) =>
      api.put<UploadedFile>(`/api/health/reports/${input.id}/file`, {
        fileName: input.fileName,
        mimeType: input.mimeType,
        contentBase64: input.contentBase64,
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['health', 'reports'] });
      showToast(`已上传 ${result.fileName}，可以开始解析了`);
      trackFunnel('report_parse', 'file_uploaded');
      trackAction('report_file_upload');
    },
    onError: (error) => {
      // 后端的中文说明（类型不支持 / 文件过大 / 内容为空）直接展示，不改写
      showToast(error instanceof ApiError ? error.message : '文件上传失败，请稍后重试', 'error');
    },
  });

  const parseReport = useMutation({
    mutationFn: (id: string) => api.post<{ id: string; parseStatus: string; message: string }>(`/api/health/reports/${id}/parse`),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['health', 'reports'] });
      showToast(result.message || '已开始解析，完成后会通知您');
      trackFunnel('report_parse', 'parse_started');
      trackAction('report_parse_start');
    },
    onError: (error) => {
      showToast(error instanceof ApiError ? error.message : '触发解析失败，请稍后重试', 'error');
    },
  });

  /** 读取本地文件并上传（base64）。前端先做一次大小与类型检查，给出即时反馈。 */
  function handleFilePicked(event: ChangeEvent<HTMLInputElement>) {
    const input = event.target;
    const file = input.files?.[0];
    const reportId = uploadForRef.current;
    input.value = ''; // 允许再次选择同一文件
    if (!file || !reportId) return;

    if (file.size > maxFileBytes) {
      showToast(`文件过大（${Math.round(file.size / 1024 / 1024)}MB），上限 20MB`, 'error');
      return;
    }
    const mimeType = inferMime(file);
    if (!mimeType) {
      showToast('不支持的文件类型，请上传 PDF、JPEG、PNG、WebP 或 HEIC', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => showToast('读取文件失败，请重试', 'error');
    reader.onload = () => {
      const dataUrl = String(reader.result ?? '');
      const base64 = dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : '';
      if (!base64) {
        showToast('读取文件失败，请重试', 'error');
        return;
      }
      uploadFile.mutate({ id: reportId, fileName: file.name, mimeType, contentBase64: base64 });
    };
    reader.readAsDataURL(file);
  }

  function pickFileFor(reportId: string) {
    uploadForRef.current = reportId;
    fileInputRef.current?.click();
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const cleanTitle = title.trim();
    if (cleanTitle.length < 2) {
      setFormError('请填写报告标题（至少 2 个字，便于日后在档案中检索）。');
      return;
    }
    if (!reportedAt) {
      setFormError('请选择报告日期。');
      return;
    }
    setFormError(null);
    createReport.mutate({
      title: cleanTitle,
      institution: institution.trim(),
      reportedAt,
      desc: desc.trim(),
    });
  }

  const header = (
    <div className="scr-head">
      <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
        <svg viewBox="0 0 24 24">
          <path d="M15 5l-7 7 7 7" />
        </svg>
      </button>
      <div className="scr-title">
        检测报告<small>HEALTH REPORTS · 解析链路与档案</small>
      </div>
    </div>
  );

  if (reportsQuery.isLoading) {
    return (
      <div className="page">
        {header}
        <div className="skeleton skel-card" />
        <div className="skeleton skel-card" style={{ height: 200 }} />
      </div>
    );
  }

  if (reportsQuery.isError || !reportsQuery.data) {
    return (
      <div className="page">
        {header}
        <StateBox
          kind="error"
          title="报告列表加载失败"
          desc="未能取回检测报告档案，请检查网络后重试。"
          action={
            <button type="button" className="btn sm" onClick={() => void reportsQuery.refetch()}>
              重新加载
            </button>
          }
        />
      </div>
    );
  }

  const reports = [...reportsQuery.data].sort((a, b) => (a.reportedAt < b.reportedAt ? 1 : -1));
  const parsingCount = reports.filter((r) => r.parseStatus === 'parsing').length;
  const failedCount = reports.filter((r) => r.parseStatus === 'failed').length;
  const awaitingCount = reports.filter((r) => r.parseStatus === 'pending').length;

  return (
    <div className="page">
      {header}

      {/* 隐藏的文件选择器：每张报告卡片按需触发，选中后由 handleFilePicked 读取并上传 */}
      <input ref={fileInputRef} type="file" accept={acceptAttr} hidden onChange={handleFilePicked} />

      {/* ---------------- 报告列表 ---------------- */}
      <Card
        title="我的检测报告"
        more={
          reports.length === 0 ? (
            <Badge tone="ghost">暂无档案</Badge>
          ) : parsingCount > 0 ? (
            <Badge tone="warn">{parsingCount} 份解析中</Badge>
          ) : failedCount > 0 ? (
            <Badge tone="danger">{failedCount} 份解析失败</Badge>
          ) : awaitingCount > 0 ? (
            <Badge tone="warn">{awaitingCount} 份待处理</Badge>
          ) : (
            <Badge tone="ok">全部已解析</Badge>
          )
        }
      >
        {reports.length === 0 ? (
          <StateBox title="尚无检测报告" desc="用下方表单建立报告档案，再上传报告文件即可开始解析。" />
        ) : (
          reports.map((report) => {
            const badge = parseBadge(report.parseStatus);
            const uploading = uploadFile.isPending && uploadForRef.current === report.id;
            const parsing = parseReport.isPending && parseReport.variables === report.id;
            return (
              <div key={report.id} style={{ padding: '13px 0', borderBottom: '1px solid var(--line)' }}>
                <div className="row between" style={{ alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.5 }}>{report.title}</span>
                  <span className="row" style={{ gap: 6, flex: '0 0 auto' }}>
                    {report.isCurrent && <Badge tone="h">当前基准</Badge>}
                    <Badge tone={badge.tone}>{badge.text}</Badge>
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
                  {report.institution || '机构未填写'} · 报告日期 {formatDate(report.reportedAt)}
                </div>
                {report.desc && (
                  <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.75, marginTop: 7 }}>
                    {report.desc}
                  </div>
                )}
                {/*
                  文件与解析信息分开表述：已解析的报告不再提「是否上传文件」，
                  否则会出现「已解析」与「尚未上传报告文件」并存的自相矛盾表述
                  （历史演示数据里确实存在这种组合）。
                */}
                <div style={{ fontSize: 12, color: 'var(--muted-2)', marginTop: 6 }}>
                  {report.parseStatus === 'parsed' ? (
                    <>
                      {report.parsedCount !== null ? `已录入 ${report.parsedCount} 项指标` : '已完成解析'}
                      {report.parsedAt ? ` · 解析于 ${formatDateTime(report.parsedAt)}` : ''}
                    </>
                  ) : report.hasFile ? (
                    `文件：${report.fileName ?? '已上传'}`
                  ) : (
                    '尚未上传报告文件'
                  )}
                </div>

                {/* 解析失败：如实展示后端给的原因，不弱化、不改写 */}
                {report.parseStatus === 'failed' && report.parseError && (
                  <div
                    style={{
                      marginTop: 9,
                      padding: '9px 11px',
                      borderRadius: 9,
                      border: '1px solid var(--danger)',
                      background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
                      fontSize: 12.5,
                      lineHeight: 1.7,
                      color: 'var(--text)',
                    }}
                  >
                    {report.parseError}
                  </div>
                )}

                {/* 同一时刻只给一个正确的下一步 */}
                {report.parseStatus !== 'parsed' && (
                  <div style={{ marginTop: 11 }}>
                    {report.parseStatus === 'parsing' ? (
                      <div className="sec-note" style={{ marginTop: 0 }}>
                        正在解析中，完成后会在消息中心通知您，本页会自动刷新。
                      </div>
                    ) : !report.hasFile ? (
                      <Button small disabled={uploading} onClick={() => pickFileFor(report.id)}>
                        {uploading ? '上传中…' : '上传报告文件'}
                      </Button>
                    ) : (
                      <Button small disabled={parsing} onClick={() => parseReport.mutate(report.id)}>
                        {parsing ? '提交中…' : report.parseStatus === 'failed' ? '重新解析' : '开始解析'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
        {reports.some((r) => r.parseStatus === 'pending' && !r.hasFile) && (
          <div className="sec-note">
            报告需要先上传文件才能解析 —— 解析服务读取的是报告原件，只填元信息无法产出指标。
          </div>
        )}
      </Card>

      {/* ---------------- 提交新报告 ---------------- */}
      <Card title="提交新报告">
        <form onSubmit={submit}>
          <div className="field">
            <label className="field-label" htmlFor="report-title">
              报告标题（必填）
            </label>
            <input
              id="report-title"
              className="field-input"
              type="text"
              maxLength={80}
              value={title}
              placeholder="例如：年度深度检测"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="report-institution">
              检测机构
            </label>
            <input
              id="report-institution"
              className="field-input"
              type="text"
              maxLength={80}
              value={institution}
              placeholder="例如：合作三甲医院 / 医学检测中心"
              onChange={(e) => setInstitution(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="report-date">
              报告日期
            </label>
            <input
              id="report-date"
              className="field-input"
              type="date"
              value={reportedAt}
              onChange={(e) => setReportedAt(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="report-desc">
              备注
            </label>
            <input
              id="report-desc"
              className="field-input"
              type="text"
              maxLength={300}
              value={desc}
              placeholder="例如：含功能医学与端粒长度检测"
              onChange={(e) => setDesc(e.target.value)}
            />
          </div>

          {formError && <div className="field-error">{formError}</div>}

          <Button type="submit" block variant="primary" disabled={createReport.isPending}>
            {createReport.isPending ? '提交中…' : '建立报告档案'}
          </Button>
        </form>

        <div className="sec-note">
          这里只建立报告档案（标题、机构、日期）。建档后在报告卡片上**上传报告文件**，
          再点「开始解析」——解析服务读取的是报告原件，系统不会凭标题猜内容，也不会生成任何占位数值。
          解析完成后指标自动录入档案并通知您，全过程在数据授权范围内进行并留下审计记录。
        </div>
      </Card>

      <div className="note-src">
        <b>数据来源与合规边界</b>：报告标题、机构、日期与文件均为您自行提交的信息，解析状态与录入的指标来自平台解析服务。
        所有检测均由具备资质的合作医疗机构执行，平台不直接开展临床诊疗，本页内容不构成诊断或治疗建议；
        解析出的指标以报告原文为准，供健康管理参考。
      </div>

      {/* 建档成功后的下一步说明（接口返回的 note 原文） */}
      <Sheet open={queued !== null} title="报告已建档" onClose={() => setQueued(null)}>
        {queued && (
          <>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>《{queued.title}》</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.8 }}>{queued.note}</div>
            <div className="sec-note">
              下一步：在上方报告卡片点「上传报告文件」选择 PDF 或照片，再点「开始解析」。
              解析在后台进行，本页会自动刷新，完成后还会在消息中心通知您。
            </div>
            <div className="sp12" />
            <Button block variant="primary" onClick={() => setQueued(null)}>
              知道了
            </Button>
          </>
        )}
      </Sheet>
    </div>
  );
}
